"use client";

import Link from "next/link";
import { useState, useEffect, useRef, type ComponentProps } from "react";
import { usePathname } from "next/navigation";
import { Search, Menu, X, ChevronRight, ArrowRight, Users } from "lucide-react";
import Logo from "./Logo";
import NavbarAuth from "./NavbarAuth";
import { PRIMARY_COMMUNITY_HREF } from "@/lib/community";
import {
  primaryNav,
  frequencySensitiveNav,
  type NavWorkspace,
} from "@/lib/navigation";

const NavLink = (props: ComponentProps<typeof Link>) => (
  <Link {...props} prefetch={false} />
);

function WorkspacePanel({
  workspace,
  onClose,
}: {
  workspace: NavWorkspace;
  onClose: () => void;
}) {
  return (
    <div className="navbar-panel">
      <NavLink
        href={workspace.href}
        className="navbar-panel-hub"
        onClick={onClose}
      >
        <span className="navbar-panel-hub-label">{workspace.label}</span>
        <ArrowRight size={14} aria-hidden="true" />
      </NavLink>
      <div className="navbar-panel-tools">
        {workspace.panelTools.map((tool) => (
          <NavLink
            key={tool.href}
            href={tool.href}
            className="navbar-panel-tool"
            onClick={onClose}
          >
            <span className="navbar-panel-tool-label">{tool.label}</span>
            {tool.description && (
              <span className="navbar-panel-tool-desc">{tool.description}</span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function openCommandPalette() {
  document.dispatchEvent(new CustomEvent("openCommandPalette"));
}

export default function Navbar() {
  const pathname = usePathname();
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActivePanel(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
    setActivePanel(null);
    setExpandedGroup(null);
  }, [pathname]);

  useEffect(() => {
    document.body.classList.toggle("mobile-drawer-open", drawerOpen);
    return () => document.body.classList.remove("mobile-drawer-open");
  }, [drawerOpen]);

  function closeAll() {
    setActivePanel(null);
    setDrawerOpen(false);
    setExpandedGroup(null);
  }

  function togglePanel(label: string) {
    setActivePanel((prev) => (prev === label ? null : label));
  }

  function isWorkspaceActive(workspace: NavWorkspace): boolean {
    if (!pathname) return false;
    if (pathname === workspace.href || pathname.startsWith(workspace.href + "/")) {
      return true;
    }
    return workspace.panelTools.some(
      (tool) => pathname === tool.href || pathname.startsWith(tool.href + "/")
    );
  }

  function isLinkActive(href: string): boolean {
    if (!pathname) return false;
    return pathname === href || pathname.startsWith(href + "/");
  }

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-container">
        {/* Logo */}
        <NavLink href="/" aria-label="ClearCurve home" onClick={closeAll}>
          <div className="navbar-logo">
            <Logo variant="full" size="lg" />
          </div>
        </NavLink>

        {/* Desktop links */}
        <div className="navbar-links">
          {primaryNav.map((item) => {
            if (item.type === "workspace") {
              const isOpen = activePanel === item.label;
              const isActive = isWorkspaceActive(item);
              return (
                <div
                  key={item.label}
                  className={`navbar-dropdown${isOpen ? " navbar-dropdown--open" : ""}${isActive ? " active" : ""}`}
                >
                  <button
                    type="button"
                    className="navbar-dropdown-trigger"
                    aria-expanded={isOpen}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => togglePanel(item.label)}
                  >
                    {item.label}
                    <ChevronRight
                      size={14}
                      aria-hidden="true"
                      className={`navbar-caret${isOpen ? " navbar-caret--open" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <WorkspacePanel workspace={item} onClose={closeAll} />
                  )}
                </div>
              );
            }
            const isActive = isLinkActive(item.href);
            return (
              <NavLink
                key={item.label}
                href={item.href}
                onClick={closeAll}
                className={isActive ? "active" : undefined}
                aria-current={isActive ? "page" : undefined}
              >
                {item.label}
              </NavLink>
            );
          })}
        </div>

        {/* Right: search + auth */}
        <div className="navbar-right">
          <button
            type="button"
            className="navbar-search-btn"
            aria-label="Quick search (Ctrl+K)"
            onClick={openCommandPalette}
          >
            <Search size={15} aria-hidden="true" />
            <span className="navbar-search-label">Search</span>
            <kbd className="navbar-search-kbd">K</kbd>
          </button>
          <a
            className="navbar-community-link"
            href={PRIMARY_COMMUNITY_HREF}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Users size={15} aria-hidden="true" />
            <span>Join WhatsApp</span>
          </a>
          <NavbarAuth />
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="navbar-mobile-toggle"
          aria-label={drawerOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((v) => !v)}
        >
          {drawerOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          <span>{drawerOpen ? "Close" : "Menu"}</span>
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="navbar-drawer" role="dialog" aria-label="Navigation menu">
          <div className="navbar-drawer-inner">
            {/* Quick jump */}
            <button
              type="button"
              className="navbar-drawer-search"
              onClick={() => {
                setDrawerOpen(false);
                openCommandPalette();
              }}
            >
              <Search size={15} aria-hidden="true" />
              Search tools &amp; pages
            </button>

            {/* Primary workspaces */}
            {primaryNav.map((item) => {
              if (item.type === "workspace") {
                const isExpanded = expandedGroup === item.label;
                const isActive = isWorkspaceActive(item);
                return (
                  <div key={item.label} className="navbar-drawer-group">
                    <button
                      type="button"
                      className={`navbar-drawer-group-trigger${isExpanded ? " navbar-drawer-group-trigger--open" : ""}${isActive ? " navbar-drawer-group-trigger--active" : ""}`}
                      aria-expanded={isExpanded}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() =>
                        setExpandedGroup(isExpanded ? null : item.label)
                      }
                    >
                      {item.label}
                      <ChevronRight
                        size={14}
                        aria-hidden="true"
                        className={`navbar-caret${isExpanded ? " navbar-caret--open" : ""}`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="navbar-drawer-group-items">
                        <NavLink
                          href={item.href}
                          className="navbar-drawer-link navbar-drawer-link--hub"
                          onClick={closeAll}
                        >
                          {item.label} Workspace
                          <ArrowRight size={12} aria-hidden="true" style={{ display: "inline", marginLeft: "0.3rem" }} />
                        </NavLink>
                        {item.panelTools.map((tool) => (
                          <NavLink
                            key={tool.href}
                            href={tool.href}
                            className={`navbar-drawer-link${isLinkActive(tool.href) ? " navbar-drawer-link--active" : ""}`}
                            aria-current={isLinkActive(tool.href) ? "page" : undefined}
                            onClick={closeAll}
                          >
                            {tool.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const isActive = isLinkActive(item.href);
              return (
                <NavLink
                  key={item.label}
                  href={item.href}
                  className={`navbar-drawer-link navbar-drawer-link--top${isActive ? " navbar-drawer-link--active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                  onClick={closeAll}
                >
                  {item.label}
                </NavLink>
              );
            })}

            {/* Frequency-sensitive tools */}
            <div className="navbar-drawer-section">
              {frequencySensitiveNav.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  className={`navbar-drawer-link${isLinkActive(item.href) ? " navbar-drawer-link--active" : ""}`}
                  aria-current={isLinkActive(item.href) ? "page" : undefined}
                  onClick={closeAll}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="navbar-drawer-actions">
              <a
                className="navbar-drawer-community"
                href={PRIMARY_COMMUNITY_HREF}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeAll}
              >
                <Users size={16} aria-hidden="true" />
                Join WhatsApp
              </a>
              <NavbarAuth />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
